import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export type NotificationChannel = 'SMS' | 'EMAIL'
export type TriggerType = 'EVENT' | 'CRON'

export interface NotificationTemplate {
  channel: NotificationChannel
  templateKey: string
  providerTemplateId: string | null
  senderOverride: string | null
  subject: string | null
  body: string | null
  variablesJson: string
  triggerType: TriggerType
  eventName: string | null
  recipientAudience: string | null
  cronExpression: string | null
  enabled: boolean
  updatedAt: string
  /** Null until a CRON template's first sweep. Answers "did this run?". */
  lastCronRunAt: string | null
}

export interface NotificationTemplateUpsert {
  channel: NotificationChannel
  templateKey: string
  providerTemplateId?: string | null
  senderOverride?: string | null
  subject?: string | null
  body?: string | null
  variables?: string[]
  triggerType: TriggerType
  eventName?: string | null
  recipientAudience?: string | null
  cronExpression?: string | null
  enabled: boolean
}

export interface OutboxEntry {
  outboxId: string
  channel: NotificationChannel
  templateKey: string
  recipient: string
  status: 'PENDING' | 'SENDING' | 'SENT' | 'FAILED' | 'DEAD'
  attempts: number
  nextAttemptAt: string
  externalId: string | null
  errorMessage: string | null
  createdAt: string
  sentAt: string | null
}

/** Never contains a secret — the backend DTO has no field capable of it. */
export interface CredentialMetadata {
  configured: boolean
  hasSecret: boolean
  config: Record<string, unknown>
  updatedAt: string | null
  updatedBy: string | null
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export function useNotificationTemplates() {
  return useQuery({
    queryKey: ['admin', 'notifications', 'templates'],
    queryFn: async () => (await api.get<NotificationTemplate[]>('/v1/notification/templates')).data,
  })
}

/** Updates an existing template — path channel/templateKey must match the body. */
export function useUpdateNotificationTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: NotificationTemplateUpsert) => {
      await api.put(`/v1/notification/templates/${body.channel}/${body.templateKey}`, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] }),
  })
}

export function useCreateNotificationTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: NotificationTemplateUpsert) => {
      await api.post('/v1/notification/templates', body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] }),
  })
}

export function useDeleteNotificationTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { channel: NotificationChannel; templateKey: string }) => {
      await api.delete(`/v1/notification/templates/${params.channel}/${params.templateKey}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] }),
  })
}

// ---------------------------------------------------------------------------
// Outbox (read-only ops view)
// ---------------------------------------------------------------------------

export function useNotificationOutbox(filters: { status?: string | undefined; channel?: string | undefined }) {
  return useQuery({
    queryKey: ['admin', 'notifications', 'outbox', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.status) params.set('status', filters.status)
      if (filters.channel) params.set('channel', filters.channel)
      params.set('limit', '200')
      const res = await api.get<OutboxEntry[]>(`/v1/notification/outbox?${params.toString()}`)
      return res.data
    },
    refetchInterval: 15_000,
  })
}

// ---------------------------------------------------------------------------
// Credentials — write-only secret
// ---------------------------------------------------------------------------

export function useNotificationCredential(provider: 'SMTP' | 'MSG91') {
  return useQuery({
    queryKey: ['admin', 'notifications', 'credentials', provider],
    queryFn: async () => (await api.get<CredentialMetadata>(`/v1/notification/credentials/${provider}`)).data,
  })
}

export function useSaveNotificationCredential(provider: 'SMTP' | 'MSG91') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { config: Record<string, unknown>; secret?: string }) => {
      await api.put(`/v1/notification/credentials/${provider}`, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'credentials', provider] }),
  })
}

export interface TestSendResult {
  accepted: boolean
  /** The provider's own words when it refused — e.g. `smtp_not_configured`. */
  detail: string | null
}

/**
 * Sends one email using the saved SMTP credential — to `recipient` if given,
 * otherwise to the address on the signed-in admin's own user record.
 *
 * A refusal comes back as HTTP 200 with `accepted: false`, so `isError` here
 * means the request itself failed: 403 (missing permission), 400 (malformed
 * address, or no address on the caller's record) or 429 (the per-admin send
 * cap — the endpoint will mail an address the caller names, so it is limited).
 */
export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (recipient?: string) =>
      (await api.post<TestSendResult>('/v1/notification/credentials/SMTP/test',
        recipient?.trim() ? { recipient: recipient.trim() } : {})).data,
  })
}
